<template>
  <div class="login-wrapper">
    <div class="login-card">
      <div class="login-header">
        <div class="user-icon-wrapper">
          <svg
            class="user-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            ></path>
          </svg>
        </div>
        <h2 class="login-title">Đăng Ký Tài Khoản</h2>
        <p class="login-subtitle">Tạo tài khoản mới để bắt đầu mua sắm</p>
      </div>

      <!-- Register Method Toggle -->
      <div class="method-toggle">
        <button
          @click="switchRegisterMethod('phone')"
          :class="['method-btn', { active: registerMethod === 'phone' }]"
        >
          Số điện thoại
        </button>
        <button
          @click="switchRegisterMethod('email')"
          :class="['method-btn', { active: registerMethod === 'email' }]"
        >
          Email
        </button>
      </div>

      <!-- Register Form -->
      <div class="form-container">
        <!-- Full Name -->
        <div class="form-group">
          <label class="form-label"
            >Họ và tên <span class="required">*</span></label
          >
          <div class="input-wrapper">
            <svg
              class="input-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              ></path>
            </svg>
            <input
              v-model="formData.name"
              type="text"
              placeholder="Nhập họ và tên"
              class="form-input"
              @blur="validateField('name')"
            />
          </div>
          <span v-if="errors.name" class="error-message">
            {{ errors.name[0] }}
          </span>
        </div>

        <!-- Phone Number Field -->
        <div v-if="registerMethod === 'phone'" class="form-group">
          <label class="form-label"
            >Số điện thoại <span class="required">*</span></label
          >
          <div class="input-wrapper">
            <svg
              class="input-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              ></path>
            </svg>
            <input
              v-model="formData.phone"
              type="tel"
              placeholder="Nhập số điện thoại"
              class="form-input"
              @blur="validateField('phone')"
            />
          </div>
          <span v-if="errors.phone" class="error-message">
            {{ errors.phone[0] }}
          </span>
        </div>

        <!-- Email Field -->
        <div v-if="registerMethod === 'email'" class="form-group">
          <label class="form-label"
            >Email <span class="required">*</span></label
          >
          <div class="input-wrapper">
            <svg
              class="input-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              ></path>
            </svg>
            <input
              v-model="formData.email"
              type="email"
              placeholder="Nhập địa chỉ email"
              class="form-input"
              @blur="validateField('email')"
            />
          </div>
          <span v-if="errors.email" class="error-message">
            {{ errors.email[0] }}
          </span>
        </div>

        <!-- OTP Verification -->
        <div v-if="showOtpVerification" class="form-group">
          <label class="form-label"
            >Mã xác thực OTP <span class="required">*</span></label
          >
          <div class="otp-container">
            <input
              v-model="formData.otp"
              type="text"
              maxlength="6"
              placeholder="Nhập mã OTP"
              class="otp-input"
            />
            <button
              type="button"
              @click="sendRegisterOTP"
              :disabled="!canResendOTP || loading"
              class="otp-button"
              :class="{ disabled: !canResendOTP }"
            >
              <span v-if="loadingOTP" class="spinner"></span>
              <span v-else>{{ otpButtonText }}</span>
            </button>
          </div>
          <span v-if="errors.otp" class="error-message">
            {{ errors.otp[0] }}
          </span>
          <p v-if="otpSent && !canResendOTP" class="info-message">
            {{ formattedCountdown }}
          </p>
        </div>

        <!-- Password Field -->
        <div class="form-group">
          <label class="form-label"
            >Mật khẩu <span class="required">*</span></label
          >
          <div class="input-wrapper">
            <svg
              class="input-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              ></path>
            </svg>
            <input
              v-model="formData.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Nhập mật khẩu (tối thiểu 6 ký tự)"
              class="form-input password-input"
              @blur="validateField('password')"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="password-toggle"
            >
              <svg
                v-if="!showPassword"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                ></path>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                ></path>
              </svg>
              <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                ></path>
              </svg>
            </button>
          </div>
          <span v-if="errors.password" class="error-message">
            {{ errors.password[0] }}
          </span>
          <!-- Password Strength Indicator -->
          <div v-if="formData.password" class="password-strength">
            <div class="strength-bar" :class="passwordStrength.class"></div>
            <span class="strength-text">{{ passwordStrength.text }}</span>
          </div>
        </div>

        <!-- Confirm Password Field -->
        <div class="form-group">
          <label class="form-label"
            >Xác nhận mật khẩu <span class="required">*</span></label
          >
          <div class="input-wrapper">
            <svg
              class="input-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <input
              v-model="formData.password_confirmation"
              :type="showConfirmPassword ? 'text' : 'password'"
              placeholder="Nhập lại mật khẩu"
              class="form-input password-input"
              @blur="validateField('password_confirmation')"
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="password-toggle"
            >
              <svg
                v-if="!showConfirmPassword"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                ></path>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                ></path>
              </svg>
              <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                ></path>
              </svg>
            </button>
          </div>
          <span v-if="errors.password_confirmation" class="error-message">
            {{ errors.password_confirmation[0] }}
          </span>
        </div>

        <!-- Terms and Conditions -->
        <div class="form-group">
          <label class="remember-checkbox">
            <input
              v-model="formData.agree_terms"
              type="checkbox"
              class="checkbox"
            />
            <span class="checkbox-label">
              Tôi đồng ý với
              <a href="#" class="link-text">Điều khoản sử dụng</a> và
              <a href="#" class="link-text">Chính sách bảo mật</a>
            </span>
          </label>
          <span v-if="errors.agree_terms" class="error-message">
            {{ errors.agree_terms[0] }}
          </span>
        </div>

        <!-- Submit Button -->
        <button
          @click="handleRegister"
          :disabled="loading || !formData.agree_terms"
          class="submit-btn"
          :class="{ loading: loading, disabled: !formData.agree_terms }"
        >
          <span v-if="loading" class="spinner"></span>
          <span v-else>Đăng Ký</span>
        </button>
      </div>

      <!-- Divider -->
      <div class="divider">
        <div class="divider-line">
          <div class="divider-border"></div>
        </div>
        <div class="divider-text-wrapper">
          <span class="divider-text">Hoặc</span>
        </div>
      </div>

      <!-- Login Section -->
      <div class="register-section">
        <p class="register-text">
          Đã có tài khoản?
          <router-link to="/login-admin.html" class="register-link">
            Đăng nhập ngay
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  data() {
    return {
      registerMethod: "phone", // 'phone' hoặc 'email'
      formData: {
        name: "",
        phone: "",
        email: "",
        password: "",
        password_confirmation: "",
        otp: "",
        agree_terms: false,
      },
      showPassword: false,
      showConfirmPassword: false,
      showOtpVerification: false,
      otpSent: false,
      countdown: 0,
      errors: {},
      loading: false,
      loadingOTP: false,
    };
  },
  computed: {
    canResendOTP() {
      return this.countdown === 0;
    },

    formattedCountdown() {
      return `Gửi lại sau ${this.countdown}s`;
    },

    otpButtonText() {
      if (!this.otpSent) {
        return "Gửi mã";
      }
      return this.canResendOTP ? "Gửi lại" : this.formattedCountdown;
    },

    passwordStrength() {
      const password = this.formData.password;
      if (!password) return { class: "", text: "" };

      let strength = 0;
      if (password.length >= 6) strength++;
      if (password.length >= 10) strength++;
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
      if (/\d/.test(password)) strength++;
      if (/[^a-zA-Z\d]/.test(password)) strength++;

      if (strength <= 2) {
        return { class: "weak", text: "Yếu" };
      } else if (strength <= 3) {
        return { class: "medium", text: "Trung bình" };
      } else {
        return { class: "strong", text: "Mạnh" };
      }
    },
  },
  methods: {
    validateForm() {
      let isValid = true;
        this.errors = {};
      if (!this.formData.name || this.formData.name.length < 2) {
        this.errors.name = ["Họ tên phải có ít nhất 2 ký tự"];
        isValid = false;
      }

      // Validate phone or email
      if (this.registerMethod === "phone") {
        if (!this.validatePhone()) {
          isValid = false;
        }
      } else {
        if (!this.validateEmail()) {
          isValid = false;
        }
      }

      // Validate password
      if (!this.formData.password || this.formData.password.length < 6) {
        this.errors.password = ["Mật khẩu phải có ít nhất 6 ký tự"];
        isValid = false;
      }

      // Validate password confirmation
      if (this.formData.password !== this.formData.password_confirmation) {
        this.errors.password_confirmation = ["Mật khẩu xác nhận không khớp"];
        isValid = false;
      }

      // Validate OTP
      if (this.showOtpVerification && !this.formData.otp) {
        this.errors.otp = ["Vui lòng nhập mã OTP"];
        isValid = false;
      }

      // Validate terms
      if (!this.formData.agree_terms) {
        this.errors.agree_terms = ["Bạn phải đồng ý với điều khoản sử dụng"];
        isValid = false;
      }

      if (!isValid) {
        this.$toast.error("Vui lòng kiểm tra lại thông tin!");
      }

      return isValid;
    },

    // Validate từng field
    validateField(field) {
      switch (field) {
        case "name":
          if (!this.formData.name || this.formData.name.length < 2) {
            this.errors.name = ["Họ tên phải có ít nhất 2 ký tự"];
          } else {
            this.errors.name = null;
          }
          break;
        case "phone":
          this.validatePhone();
          break;
        case "email":
          this.validateEmail();
          break;
        case "password":
          if (!this.formData.password || this.formData.password.length < 6) {
            this.errors.password = ["Mật khẩu phải có ít nhất 6 ký tự"];
          } else {
            this.errors.password = null;
          }
          break;
        case "password_confirmation":
          if (this.formData.password !== this.formData.password_confirmation) {
            this.errors.password_confirmation = [
              "Mật khẩu xác nhận không khớp",
            ];
          } else {
            this.errors.password_confirmation = null;
          }
          break;
      }
    },

    // Validate số điện thoại
    validatePhone() {
      const phoneRegex = /(84|0[3|5|7|8|9])+([0-9]{8})\b/;

      if (!this.formData.phone) {
        this.errors.phone = ["Vui lòng nhập số điện thoại"];
        return false;
      }

      if (!phoneRegex.test(this.formData.phone)) {
        this.errors.phone = ["Số điện thoại không hợp lệ"];
        return false;
      }

      this.errors.phone = null;
      return true;
    },

    // Validate email
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!this.formData.email) {
        this.errors.email = ["Vui lòng nhập email"];
        return false;
      }

      if (!emailRegex.test(this.formData.email)) {
        this.errors.email = ["Email không hợp lệ"];
        return false;
      }

      this.errors.email = null;
      return true;
    },
    switchRegisterMethod(method) {
      this.registerMethod = method;
      this.showOtpVerification = false;
      this.errors = {};
      this.otpSent = false;
      this.countdown = 0;
      this.formData.otp = "";
      this.errors.otp = null;
    },
  },
};
</script>

<style scoped>
/* Required field indicator */
.required {
  color: #ef4444;
  margin-left: 2px;
}

/* Error Message */
.error-message {
  display: block;
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Info Message */
.info-message {
  display: block;
  color: #3b82f6;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

/* Link Text */
.link-text {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.link-text:hover {
  text-decoration: underline;
}

/* Password Strength */
.password-strength {
  margin-top: 0.5rem;
}

.strength-bar {
  height: 4px;
  border-radius: 2px;
  margin-bottom: 0.25rem;
  transition: all 0.3s;
}

.strength-bar.weak {
  width: 33%;
  background-color: #ef4444;
}

.strength-bar.medium {
  width: 66%;
  background-color: #f59e0b;
}

.strength-bar.strong {
  width: 100%;
  background-color: #10b981;
}

.strength-text {
  font-size: 0.75rem;
  font-weight: 500;
}

.strength-bar.weak + .strength-text {
  color: #ef4444;
}

.strength-bar.medium + .strength-text {
  color: #f59e0b;
}

.strength-bar.strong + .strength-text {
  color: #10b981;
}

/* Disabled States */
.otp-button.disabled,
.submit-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Loading Button */
.submit-btn.loading {
  opacity: 0.8;
  cursor: not-allowed;
}
</style>
